import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useGiftWrapContext } from '../context/GiftWrapContext';
import { usePage } from '../context/PageContext';
import { giftWrapEventListener } from '../services/giftWrapEventListener';
import { groupPackagingOptions, hasEligibleBox } from './options';
import { initialSelection, selectionReducer, getStepState, autoSelectBox } from './selection';
import { buildPackagingChoices } from './buildChoices';
import { PACKAGING_UNAVAILABLE_EVENT } from './events';

const PackagingContext = createContext(null);

/**
 * Selection state for the packaging steps. It keeps GiftWrapContext.personalisationChoices in step
 * with the selection, so the existing cart writer is reused unchanged.
 * @param {{ children: React.ReactNode }} props
 */
export function PackagingProvider({ children }) {
  const {
    wrapOptions,
    loading,
    giftWrapModalOpen,
    setGiftWrapModalOpen,
    setPersonalisationChoices,
    resetGiftWrapState,
    skipUvInsertsFlow
  } = useGiftWrapContext();
  const { goToPage } = usePage();
  const [selection, dispatch] = useReducer(selectionReducer, initialSelection);
  const [ready, setReady] = useState(false);
  const sawLoading = useRef(false);
  // The gate decides once per open. Later loading cycles (the add_gift_wrap event re-fetches while the
  // modal is still open) must not close it mid-add-to-cart.
  const decided = useRef(false);

  const groups = useMemo(() => groupPackagingOptions(wrapOptions), [wrapOptions]);
  const stepState = useMemo(() => getStepState({ selection, groups }), [selection, groups]);

  // Write the final choices in the same event as the navigation, so FINISHED never sees stale ones.
  const leaveSteps = useCallback(
    (nextSelection) => {
      setPersonalisationChoices(buildPackagingChoices({ selection: nextSelection, groups }));
      goToPage(skipUvInsertsFlow ? 'FINISHED' : 'addPersonalTouch');
    },
    [groups, goToPage, setPersonalisationChoices, skipUvInsertsFlow]
  );

  useEffect(() => {
    if (giftWrapModalOpen) return;
    dispatch({ type: 'reset' });
    setReady(false);
    sawLoading.current = false;
    decided.current = false;
  }, [giftWrapModalOpen]);

  // Show the journey only after this open's options have loaded, and only if a box passed (Q20).
  useEffect(() => {
    if (!giftWrapModalOpen || decided.current) return;
    if (loading) {
      sawLoading.current = true;
      return;
    }
    if (!sawLoading.current) return;
    sawLoading.current = false;
    decided.current = true;
    const auto = hasEligibleBox(wrapOptions) ? autoSelectBox(groups) : null;
    // One box, nothing to present and no step 3 leaves the customer nothing to choose. The cart is never
    // written without a choice, so that piece is treated like one with no eligible box.
    if (auto && !(auto.leaveSteps && skipUvInsertsFlow)) {
      // One eligible box: pick it and continue for the customer, so step 1 is never shown.
      auto.actions.forEach(dispatch);
      if (auto.leaveSteps) leaveSteps(auto.actions.reduce(selectionReducer, initialSelection));
      setReady(true);
      return;
    }
    // Tell the cart which piece has no journey, so its "Add Gift Options" can hide (ALBAU-884).
    const pending = giftWrapEventListener.pendingAddToCartData;
    const variantId = pending?.variantId ?? pending?.cartData?.variant_id ?? null;
    resetGiftWrapState();
    giftWrapEventListener.clearPendingData();
    setGiftWrapModalOpen(false);
    if (variantId != null) {
      document.dispatchEvent(new CustomEvent(PACKAGING_UNAVAILABLE_EVENT, { detail: { variantId: String(variantId) } }));
    }
  }, [
    giftWrapModalOpen,
    loading,
    wrapOptions,
    groups,
    leaveSteps,
    skipUvInsertsFlow,
    resetGiftWrapState,
    setGiftWrapModalOpen
  ]);

  useEffect(() => {
    if (!giftWrapModalOpen) return;
    setPersonalisationChoices(buildPackagingChoices({ selection, groups }));
  }, [giftWrapModalOpen, selection, groups, setPersonalisationChoices]);

  const value = useMemo(
    () => ({ selection, dispatch, groups, stepState, ready, leaveSteps }),
    [selection, groups, stepState, ready, leaveSteps]
  );
  return <PackagingContext.Provider value={value}>{children}</PackagingContext.Provider>;
}

/** @returns {{ selection: typeof initialSelection, dispatch: Function, groups: Object, stepState: ReturnType<typeof getStepState>, ready: boolean, leaveSteps: (nextSelection: typeof initialSelection) => void } | null} */
export function usePackaging() {
  return useContext(PackagingContext);
}
